import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { SFNotification, UserProfile } from 'utils/types';
import ProfileDetails from './ProfileDetails';
import NotificationDetails from './NotificationDetails';
import Profile from '../Profile';
import { isValidEmail, isEmptyString } from 'utils/text';
import { getWord } from 'utils/tenant';
import { configHasValidHelpDeskKeys } from 'utils/profile';

/**
 * @param {object} props - see propTypes
 * @returns {JSX.Element} the tabs for the student's profile
 */
function StudentProfileContainer(props) {
    const [selectedTab, selectTab] = useState(0);

    useEffect(() => {
        const invalidAlternateEmail = !isEmptyString(props.profile.preferredEmail) && !isValidEmail(props.profile.preferredEmail);
        const errors = [
            invalidAlternateEmail && `Please provide a valid ${getWord('alternateEmail', true)} address.`,
            props.invalidPronouns && 'Pronouns field cannot contain more than 25 characters.',
        ].filter(Boolean);

        const errorMessage = errors.length > 1 ? 'You have more than one error on the fields below.' : errors[0];
        if (errorMessage) {
            props.notification.error(errorMessage);
        } else {
            props.notification.dismiss();
        }
    }, [props.invalidPronouns, props.profile.preferredEmail]);

    const getTabs = () => {
        const tabs = [{
            id: 'editProfileTab',
            name: 'Edit Profile'
        }];

        if (props.showNotificationsTab)
            tabs.push({
                id: 'notificationsTab',
                name: 'Notifications'
            });

        return tabs;
    };

    // Scroll to top when selected tab changes
    useEffect(() => {
        props.studentContentRef.current.scrollTop = 0;
    }, [selectedTab]);

    return (
        <Profile notification={props.notification}
                 onSelect={tabIndex => selectTab(tabIndex)}
                 selectedTab={selectedTab}
                 tabs={getTabs()}>
            <ProfileDetails allowEditPronouns={props.allowEditPronouns}
                            allowPhotoUpload={props.allowPhotoUpload}
                            allowPreferredEmail={props.allowPreferredEmail}
                            goToNotificationsTab={() => selectTab(1)}
                            isIE={props.isIE}
                            mobileEmailInfoLink={props.mobileEmailInfoLink}
                            originalProfile={props.originalProfile}
                            photoAcceptTypes={props.photoAcceptTypes}
                            profile={props.profile}
                            setValue={props.setValue}
                            showMobileEmailInfo={props.showMobileEmailInfo}
                            showNotificationsLinks={props.showNotificationsTab}
                            showPronouns={props.showPronouns}
                            supportedCountries={props.supportedCountries}
                            textMessagingEnabled={props.textMessagingEnabled && configHasValidHelpDeskKeys(props.helpDeskInformation)}
                            validatePhoto={props.validatePhoto} />
            {props.showNotificationsTab &&
            <NotificationDetails allowPreferredEmail={props.allowPreferredEmail}
                                 goToProfileDetails={() => selectTab(0)}
                                 profile={props.profile}
                                 setValue={props.setValue}
                                 textMessagingEnabled={props.textMessagingEnabled && configHasValidHelpDeskKeys(props.helpDeskInformation)}
                                 weekendReminderEmailSettingEnabled={props.weekendReminderEmailSettingEnabled} />
            }
        </Profile>
    );
}

StudentProfileContainer.propTypes = {
    allowEditPronouns: PropTypes.bool,
    allowPhotoUpload: PropTypes.bool,
    allowPreferredEmail: PropTypes.bool,
    studentContentRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]),
    helpDeskInformation: PropTypes.string,
    invalidPronouns: PropTypes.bool,
    isIE: PropTypes.bool,
    mobileEmailInfoLink: PropTypes.string,
    notification: SFNotification.isRequired,
    originalProfile: UserProfile,
    photoAcceptTypes: PropTypes.string,
    profile: UserProfile,
    setValue: PropTypes.func.isRequired,
    showMobileEmailInfo: PropTypes.bool,
    showNotificationsTab: PropTypes.bool,
    showPronouns: PropTypes.bool,
    supportedCountries: PropTypes.string,
    textMessagingEnabled: PropTypes.bool,
    validatePhoto: PropTypes.func.isRequired,
    weekendReminderEmailSettingEnabled: PropTypes.bool.isRequired
};

export default StudentProfileContainer;
